// HaloPSA's site `maincontact_name` is the contact's display name ("Jane Doe") while the main-contact
// picker lists usernames ("jane.doe"), so names are compared with case, dots, underscores and repeated
// spaces folded away.
const normaliseName = (value) => String(value ?? '').trim().toLowerCase().replace(/[._\s]+/g, ' ');

/**
 * The picker option whose name matches `name` (a display name or a username), or null.
 */
export function findContactOptionByName(options, name) {
  if (!name || !options?.length) return null;
  const wanted = normaliseName(name);
  return options.find((option) => normaliseName(option.name) === wanted || normaliseName(option.label) === wanted) ?? null;
}

/**
 * The option to pre-select as the site's current main contact, or null when none can be told apart.
 *
 * Tried in order: the backend's `isCurrentMainContact` flag (the site's `maincontact_id`), the saved
 * contact id, the saved name, and finally the only contact when the site has just one — a site always
 * has a main contact, so a single contact is it. The flag alone was not enough: when the site's
 * `maincontact_id` is missing or stale (e.g. signup's best-effort main-contact write failed) the field
 * stayed empty although the summary showed the contact.
 *
 * @param {Array<{ value: string, name?: string, label?: string, isCurrentMainContact?: boolean }>} options
 * @param {{ id?: string|number|null, name?: string|null }} [saved]
 */
export function findCurrentMainContactOption(options, { id, name } = {}) {
  if (!options?.length) return null;

  const flagged = options.find((option) => option.isCurrentMainContact);
  if (flagged) return flagged;

  if (id !== undefined && id !== null && id !== '') {
    const byId = options.find((option) => option.value === String(id));
    if (byId) return byId;
  }

  const byName = findContactOptionByName(options, name);
  if (byName) return byName;

  return options.length === 1 ? options[0] : null;
}
